import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Target, MapPin, Clock, Users, Zap, Loader2 } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

export default function SessionsPage() {
  const { api } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quotingId, setQuotingId] = useState(null);
  const [quoteResult, setQuoteResult] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [bookResult, setBookResult] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get('/sessions?status=upcoming&limit=20');
      setSessions(res.data);
    } catch (err) {
      console.error('Sessions fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const getStakeQuote = async (sessionId) => {
    setQuotingId(sessionId);
    setQuoteResult(null);
    setBookResult(null);
    try {
      const res = await api.post('/agents/stake-quote', { session_id: sessionId });
      setQuoteResult(res.data);
    } catch (err) {
      console.error('Stake quote error:', err);
    } finally {
      setQuotingId(null);
    }
  };

  const bookSession = async () => {
    if (!quoteResult) return;
    setBookingId(quoteResult.session.id);
    try {
      const res = await api.post('/bookings', {
        session_id: quoteResult.session.id,
        stake_amount: quoteResult.stake_amount,
      });
      setBookResult(res.data);
      setQuoteResult(null);
      fetchSessions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Booking failed');
    } finally {
      setBookingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="sessions-page" className="space-y-6">
      <motion.div {...fadeUp}>
        <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight">Sessions</h1>
        <p className="text-slate-400 text-sm font-manrope mt-0.5">Upcoming sessions with smart stake quotes</p>
      </motion.div>

      {/* Stake Quote Modal */}
      {quoteResult && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-slate-950/80 backdrop-blur-xl border border-blue-500/30 rounded-xl p-5 shadow-[0_0_30px_rgba(59,130,246,0.15)]"
          data-testid="stake-quote-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-xs text-blue-400 uppercase tracking-wider font-bold">AI Stake Quote</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Session</p>
              <p className="text-white font-medium">{quoteResult.session.sport} at {quoteResult.session.venue_name}</p>
              <p className="text-slate-500 text-xs mt-0.5">{new Date(quoteResult.session.start_time).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Stake Required</p>
              <p className="font-barlow text-3xl font-bold text-lime-400">{quoteResult.stake_amount} $PLAY</p>
              <p className="text-slate-400 text-xs mt-1">{quoteResult.explanation_user}</p>
            </div>
          </div>
          <div className="flex gap-1.5 mt-3 flex-wrap">
            {(quoteResult.reason_codes || []).map((rc, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 font-mono">{rc}</span>
            ))}
          </div>
          <div className="flex gap-3 mt-4">
            <button
              data-testid="confirm-booking-btn"
              onClick={bookSession}
              disabled={!!bookingId}
              className="px-6 py-2.5 rounded-full bg-lime-500 hover:bg-lime-400 text-black font-bold text-sm uppercase tracking-wide transition-all disabled:opacity-50 shadow-[0_0_20px_rgba(190,242,100,0.3)]"
            >
              {bookingId ? 'Booking...' : 'Confirm & Stake'}
            </button>
            <button
              onClick={() => setQuoteResult(null)}
              className="px-6 py-2.5 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-all"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      )}

      {/* Booking Success */}
      {bookResult && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-lime-500/10 border border-lime-400/30 rounded-xl p-4"
          data-testid="booking-success"
        >
          <p className="text-lime-400 font-bold text-sm">{bookResult.message}</p>
        </motion.div>
      )}

      {/* Sessions Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sessions.map((session, i) => (
          <motion.div
            key={session.id}
            {...fadeUp}
            transition={{ delay: i * 0.05, duration: 0.3 }}
            className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-blue-500/20 transition-all group"
            data-testid={`session-card-${i}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-bold uppercase">
                {session.sport}
              </span>
              {session.is_off_peak && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-lime-500/20 text-lime-400">Off-Peak</span>
              )}
            </div>
            <h4 className="text-white font-medium text-sm mb-2 flex items-center gap-1.5">
              <MapPin size={14} className="text-slate-500" />
              {session.venue_name}
            </h4>
            <div className="space-y-1.5 text-xs text-slate-400 mb-3">
              <div className="flex items-center gap-1.5">
                <Clock size={12} />
                {new Date(session.start_time).toLocaleString()}
              </div>
              <div className="flex items-center gap-1.5">
                <Users size={12} />
                {session.current_bookings}/{session.capacity} players ({session.fill_rate}% full)
              </div>
              <div className="flex items-center gap-1.5">
                <Zap size={12} />
                {session.price} PHP
              </div>
            </div>
            {/* Fill Rate Bar */}
            <div className="w-full h-1.5 bg-slate-800 rounded-full mb-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  session.fill_rate >= 80 ? 'bg-pink-500' :
                  session.fill_rate >= 50 ? 'bg-blue-500' :
                  'bg-lime-400'
                }`}
                style={{ width: `${session.fill_rate}%` }}
              />
            </div>
            <button
              data-testid={`quote-btn-${i}`}
              onClick={() => getStakeQuote(session.id)}
              disabled={quotingId === session.id || session.current_bookings >= session.capacity}
              className="w-full py-2 rounded-full bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 text-xs font-bold uppercase tracking-wide transition-all disabled:opacity-40 flex items-center justify-center gap-1.5"
            >
              {quotingId === session.id ? (
                <><Loader2 size={14} className="animate-spin" /> Getting Quote...</>
              ) : session.current_bookings >= session.capacity ? (
                'Full'
              ) : (
                <><Target size={14} /> Get Stake Quote</>
              )}
            </button>
          </motion.div>
        ))}
      </div>

      {sessions.length === 0 && (
        <div className="text-center py-16 text-slate-500">
          <Target size={48} className="mx-auto mb-3 opacity-30" />
          <p>No upcoming sessions available</p>
        </div>
      )}
    </div>
  );
}
